import { useState, useEffect, useCallback } from 'react';
import { NewsItem } from '../types';
import { apiService } from '../services/api';

interface UseNewsItemsParams {
  page?: number;
  limit?: number;
  category?: string;
  published?: boolean;
}

export const useNewsItems = (params?: UseNewsItemsParams) => {
  const [newsItems, setNewsItems] = useState<NewsItem[]>([]);
  const [pagination, setPagination] = useState({
    total: 0,
    page: 1,
    limit: 10,
    totalPages: 0,
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchNewsItems = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await apiService.getNewsItems(params);
      if (response.success) {
        setNewsItems(response.data.data);
        setPagination({
          total: response.data.total,
          page: response.data.page,
          limit: response.data.limit,
          totalPages: response.data.totalPages,
        });
      } else {
        setError(response.message || 'Failed to fetch news items');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unknown error occurred');
    } finally {
      setLoading(false);
    }
  }, [params]);

  const getNewsItem = useCallback(async (id: string) => {
    setLoading(true);
    setError(null);
    try {
      const response = await apiService.getNewsItem(id);
      if (response.success) {
        return response.data;
      } else {
        setError(response.message || 'Failed to fetch news item');
        throw new Error(response.message || 'Failed to fetch news item');
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Unknown error occurred';
      setError(errorMessage);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  const createNewsItem = useCallback(async (data: Omit<NewsItem, 'id' | 'createdAt' | 'updatedAt'>) => {
    setLoading(true);
    setError(null);
    try {
      const response = await apiService.createNewsItem(data);
      if (response.success) {
        setNewsItems(prev => [response.data, ...prev]);
        setPagination(prev => ({ ...prev, total: prev.total + 1 }));
        return response.data;
      } else {
        setError(response.message || 'Failed to create news item');
        throw new Error(response.message || 'Failed to create news item');
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Unknown error occurred';
      setError(errorMessage);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  const updateNewsItem = useCallback(async (id: string, data: Partial<NewsItem>) => {
    setLoading(true);
    setError(null);
    try {
      const response = await apiService.updateNewsItem(id, data);
      if (response.success) {
        setNewsItems(prev => 
          prev.map(item => item.id === id ? response.data : item)
        );
        return response.data;
      } else {
        setError(response.message || 'Failed to update news item');
        throw new Error(response.message || 'Failed to update news item');
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Unknown error occurred';
      setError(errorMessage);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  const deleteNewsItem = useCallback(async (id: string) => {
    setLoading(true);
    setError(null);
    try {
      const response = await apiService.deleteNewsItem(id);
      if (response.success) {
        setNewsItems(prev => prev.filter(item => item.id !== id));
        setPagination(prev => ({ ...prev, total: prev.total - 1 }));
      } else {
        setError(response.message || 'Failed to delete news item');
        throw new Error(response.message || 'Failed to delete news item');
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Unknown error occurred';
      setError(errorMessage);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchNewsItems();
  }, [fetchNewsItems]);

  return {
    newsItems,
    pagination,
    loading,
    error,
    refetch: fetchNewsItems,
    getNewsItem,
    createNewsItem,
    updateNewsItem,
    deleteNewsItem,
  };
};